/**
 * What the heads-up display reads off a running match: the bar at the top of the screen while
 * it is being played, and the figures the full-time screen puts up when it is over. All of it
 * is arithmetic over match state with no DOM anywhere, so it can be tested without a browser;
 * the elements themselves live in `shell.ts`.
 *
 * Everything clock-related reads `Match.matchTimeMs`, the displayed football clock, never
 * `Match.timeMs`, the raw simulation clock. The two run at different rates whenever the
 * match-duration setting departs from real time (`durationFactor` in `sim/match.ts`), which
 * is every match except the one where a player drags the duration slider to its slowest
 * setting. See `match.cpp:1281-1290` for the original's own clock string.
 */

import { Vector3 } from 'three';
import { PITCH } from '../core/constants';
import type { PlayerRole } from '../sim/formation';
import type { Match } from '../sim/match';
import type { RestartState } from '../sim/referee';
import { SetPiece } from '../sim/setpiece';

function pad2(value: number): string {
  return value < 10 ? `0${value}` : `${value}`;
}

/** `MM:SS`, both halves zero-padded to two digits — `match.cpp:1281-1290`. Never caps at 90. */
export function formatMatchClock(matchTimeMs: number): string {
  const totalSeconds = Math.floor(matchTimeMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${pad2(minutes)}:${pad2(seconds)}`;
}

export interface ScoreboardText {
  clock: string;
  score: string;
}

/**
 * The scoreboard's two live fields: the clock and the score line. `scoreboard.cpp:56-60`
 * shows the same information — a clock plus each side's short name and goal count — as one
 * bar; the original has no phase indicator on it (half/extra-time changes surface through a
 * separate full-screen interstitial, `phasemenu.cpp:14`, that this shell does not implement).
 */
export function scoreboardText(
  match: Pick<Match, 'matchTimeMs' | 'teams'>,
  homeShortName: string,
  awayShortName: string,
): ScoreboardText {
  return {
    clock: formatMatchClock(match.matchTimeMs),
    score: `${homeShortName}  ${match.teams[0].score} – ${match.teams[1].score}  ${awayShortName}`,
  };
}

/** A caution or dismissal just shown, for the on-screen banner to announce. */
export interface CardEvent {
  teamIndex: 0 | 1;
  role: PlayerRole;
  kind: 'yellow' | 'second-yellow' | 'red';
}

export type CardWatchView = Pick<Match, 'teams'>;

/**
 * Watches every player's own card count and reports each new one the moment it is shown.
 *
 * `MatchPlayer.cards` only ever grows — 1 for a caution, 2 for a second yellow, 3 for a
 * straight red (`sim/match.ts`) — so comparing it against what was last seen catches a new
 * card exactly once with no callback out of the referee. Unlike `MatchStatsTracker` below,
 * which must see every simulation tick because a touch is a transient instant, a card count
 * stays raised once it rises, so sampling once a rendered frame from `GameShell.updateHud`
 * cannot miss one even if a frame is skipped.
 */
export class CardWatcher {
  private readonly lastSeen = new Map<number, number>();

  /** Forget every count, so a new match does not read a returning shirt number as pre-booked. */
  reset(): void {
    this.lastSeen.clear();
  }

  sample(match: CardWatchView): CardEvent[] {
    const events: CardEvent[] = [];
    for (let i = 0; i < match.teams.length; i++) {
      const teamIndex = i === 0 ? 0 : 1;
      // The shell's own HUD tests build a team out of just a score, the one field every other
      // reader of `HudMatchView` has ever needed — so a missing roster means "nothing to
      // report" rather than a broken fixture.
      for (const player of match.teams[i]!.players ?? []) {
        const before = this.lastSeen.get(player.id) ?? 0;
        if (player.cards > before) {
          const kind = player.cards >= 3 ? 'red' : player.cards === 2 ? 'second-yellow' : 'yellow';
          events.push({ teamIndex, role: player.formationEntry.role, kind });
        }
        this.lastSeen.set(player.id, player.cards);
      }
    }
    return events;
  }
}

/** A goal just scored, for the on-screen banner to announce. */
export interface GoalEvent {
  teamIndex: 0 | 1;
}

export type GoalWatchView = Pick<Match, 'teams'>;

/**
 * Watches each side's own score and reports a new goal the moment it goes on the board.
 *
 * `TeamState.score` only ever grows — it is incremented once, by the referee giving a goal
 * (`referee.ts:348`), and never reset mid-match — so comparing it against what was last seen
 * catches a new goal exactly once with no callback out of the referee. The same trick
 * `CardWatcher` above plays on `MatchPlayer.cards`.
 */
export class GoalWatcher {
  private readonly lastSeen: [number, number] = [0, 0];

  /** Forget both scores, so a new match does not read 0-0 as a fall from a previous lead. */
  reset(): void {
    this.lastSeen[0] = 0;
    this.lastSeen[1] = 0;
  }

  sample(match: GoalWatchView): GoalEvent[] {
    const events: GoalEvent[] = [];
    for (let i = 0; i < match.teams.length; i++) {
      const teamIndex = i === 0 ? 0 : 1;
      const score = match.teams[i]!.score;
      if (score > this.lastSeen[teamIndex]) events.push({ teamIndex });
      this.lastSeen[teamIndex] = score;
    }
    return events;
  }
}

// ---------------------------------------------------------------------------
// What the full-time screen puts up
// ---------------------------------------------------------------------------

/** A shot has to be struck within this angle of the middle of the goal to count as one. */
const SHOT_MAX_ANGLE_RADIANS = (35 * Math.PI) / 180;
/** ...and from no further out than this. Beyond it, it is a clearance or a hopeful punt. */
const SHOT_MAX_RANGE_M = 45;
/** How far outside the painted line the ball has to be before it has left the field. */
const OUT_MARGIN = PITCH.lineHalfWidth + 0.11;

/** One side's column in the full-time summary. Possession is a whole percentage. */
export interface TeamStatLine {
  possession: number;
  shots: number;
  shotsOnTarget: number;
  corners: number;
}

export interface MatchStatsSnapshot {
  home: TeamStatLine;
  away: TeamStatLine;
}

export type MatchStatsView = Pick<
  Match,
  'inPlay' | 'inSetPiece' | 'designatedPossessionPlayer' | 'teams' | 'pendingRestart' | 'ball' | 'team'
>;

/**
 * The four figures a full-time graphic shows, counted from outside the simulation.
 *
 * NOTHING IN THE MATCH COUNTS ANY OF THIS. It knows who is nearest the ball this instant and
 * it knows the score, and that is all; there is no running possession total and no notion of
 * "an attempt on goal" anywhere in it. So these are watched from here, one simulation tick at
 * a time, exactly the way the whole-match measuring harness watches them
 * (`tools/measure/observe.ts`) — and deliberately by the same rules, so that what a player
 * reads on the screen is the same number `npm run measure` reports for that fixture.
 *
 * The two judgements worth stating, because neither is a fact the game records:
 *
 *  - A SHOT is a ball contact whose animation is a shot AND which sends the ball within 35
 *    degrees of the middle of the opposing goal from inside 45 metres. The same animation is
 *    used to hoof the ball clear, so without that filter a panicking defender's clearance
 *    counts as an attempt on goal.
 *  - ON TARGET is read out of the ball's own three-second forecast, which is rebuilt the
 *    instant the ball is struck: would it have crossed the line, left alone? The forecast
 *    knows about the woodwork and the ground and nothing about the keeper, which is right —
 *    a save is still a shot on target.
 *
 * POSSESSION counts only the ticks the ball was live. The harness counts every tick; sharing
 * out the dead ball between two sides means nothing to anybody watching.
 *
 * `sample` must see EVERY tick. Every figure here is a change between two of them, so a
 * skipped tick loses whatever happened on it with no symptom at all.
 */
export class MatchStatsTracker {
  private readonly possessionTicks: [number, number] = [0, 0];
  private readonly shots: [number, number] = [0, 0];
  private readonly shotsOnTarget: [number, number] = [0, 0];
  private readonly corners: [number, number] = [0, 0];
  /** Player id to the touch time already counted, so one contact is not counted twice. */
  private readonly countedTouchMs = new Map<number, number>();
  private lastRestart: RestartState | null = null;

  private readonly goalCentre = new Vector3();
  private readonly toGoal = new Vector3();
  private readonly outgoing = new Vector3();
  private readonly probe = new Vector3();
  private readonly previousProbe = new Vector3();

  sample(match: MatchStatsView): void {
    if (match.inPlay && !match.inSetPiece) {
      this.possessionTicks[match.designatedPossessionPlayer.teamId === 0 ? 0 : 1]++;
    }

    // A restart is an object that appears, so a corner is a new one of the right kind. The
    // side it is awarded to is the side that gets the corner.
    const restart = match.pendingRestart;
    if (restart && restart !== this.lastRestart && restart.type === SetPiece.Corner) {
      this.corners[restart.teamId === 0 ? 0 : 1]++;
    }
    this.lastRestart = restart;

    for (const team of match.teams) {
      for (const player of team.players) {
        const counted = this.countedTouchMs.get(player.id) ?? 0;
        if (player.lastTouchTimeMs <= counted) continue;
        this.countedTouchMs.set(player.id, player.lastTouchTimeMs);
        if (player.anim.currentFunctionType !== 'shot') continue;

        // `side` on a team is the goal it DEFENDS, and it flips at half time, so the goal
        // this player is attacking has to be asked for every time.
        const attackingSide = -match.team(player.teamId).side;
        this.goalCentre.set(PITCH.halfLength * attackingSide, 0, 0);
        if (player.position.distanceTo(this.goalCentre) > SHOT_MAX_RANGE_M) continue;

        this.toGoal.copy(this.goalCentre).sub(match.ball.position).setZ(0);
        this.outgoing.copy(match.ball.momentum).setZ(0);
        if (this.toGoal.lengthSq() === 0 || this.outgoing.lengthSq() === 0) continue;
        if (
          this.toGoal.normalize().dot(this.outgoing.normalize()) <
          Math.cos(SHOT_MAX_ANGLE_RADIANS)
        ) {
          continue;
        }

        const index = player.teamId === 0 ? 0 : 1;
        this.shots[index]++;
        if (this.headedIn(match, attackingSide)) this.shotsOnTarget[index]++;
      }
    }
  }

  snapshot(): MatchStatsSnapshot {
    const played = this.possessionTicks[0] + this.possessionTicks[1];
    // Before a ball has been kicked the honest answer is even, which is what a real graphic
    // shows too. The two always sum to a hundred, so no rounding leaves 99% on the screen.
    const home = played === 0 ? 50 : Math.round((this.possessionTicks[0] / played) * 100);
    return {
      home: {
        possession: home,
        shots: this.shots[0],
        shotsOnTarget: this.shotsOnTarget[0],
        corners: this.corners[0],
      },
      away: {
        possession: 100 - home,
        shots: this.shots[1],
        shotsOnTarget: this.shotsOnTarget[1],
        corners: this.corners[1],
      },
    };
  }

  /** Would this ball have crossed the line, left alone? Walks the ball's own forecast. */
  private headedIn(match: MatchStatsView, attackingSide: number): boolean {
    const plane = (PITCH.halfLength + OUT_MARGIN) * attackingSide;
    match.ball.predict(0, this.previousProbe);
    for (let timeMs = 10; timeMs <= 3000; timeMs += 10) {
      match.ball.predict(timeMs, this.probe);
      const crossed =
        attackingSide > 0
          ? this.previousProbe.x < plane && this.probe.x >= plane
          : this.previousProbe.x > plane && this.probe.x <= plane;
      if (crossed && this.probe.x !== this.previousProbe.x) {
        const u = (plane - this.previousProbe.x) / (this.probe.x - this.previousProbe.x);
        const y = this.previousProbe.y + (this.probe.y - this.previousProbe.y) * u;
        const z = this.previousProbe.z + (this.probe.z - this.previousProbe.z) * u;
        if (Math.abs(y) <= PITCH.goalHalfWidth && z >= 0 && z <= PITCH.goalHeight) return true;
      }
      this.previousProbe.copy(this.probe);
    }
    return false;
  }
}
